import { InputAdornment, ListSubheader, MenuItem, TextField, useMediaQuery } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFilterDropdown } from "../hooks/useFilterDropdown";
import { CategorySelectOption, categoryDisplayName, sortCategoriesHierarchically } from "../lib/format";
import type { Category } from "../lib/types";

type CategorySearchSelectProps = {
  categories: Category[];
  allCategories?: Category[];
  value: string;
  label: ReactNode;
  emptyLabel: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: "small" | "medium";
  className?: string;
  startAdornment?: ReactNode;
  renderValueIcon?: boolean;
  disableMobileSearchAutoFocus?: boolean;
  onChange: (categoryId: string) => void;
};

function normalizeSearch(value: string) {
  return value.trim().toLowerCase();
}

function categoryMatches(category: Category, allCategories: Category[], query: string) {
  if (!query) return true;
  return categoryDisplayName(category, allCategories).toLowerCase().includes(query);
}

function filterCategories(categories: Category[], allCategories: Category[], query: string) {
  if (!query) return sortCategoriesHierarchically(categories, allCategories);

  const byId = new Map(categories.map((category) => [category.id, category]));
  const matchedIds = new Set<string>();
  for (const category of categories) {
    if (!categoryMatches(category, allCategories, query)) continue;
    matchedIds.add(category.id);
    if (category.parent_id && byId.has(category.parent_id)) {
      matchedIds.add(category.parent_id);
    }
  }

  return sortCategoriesHierarchically(
    categories.filter((category) => matchedIds.has(category.id)),
    allCategories,
  );
}

export function CategorySearchSelect({
  categories,
  allCategories = categories,
  value,
  label,
  emptyLabel,
  disabled,
  error,
  helperText,
  size,
  className,
  startAdornment,
  renderValueIcon = true,
  disableMobileSearchAutoFocus = false,
  onChange,
}: CategorySearchSelectProps) {
  const isMobile = useMediaQuery("(max-width: 980px)");
  const dropdown = useFilterDropdown({
    paperClass: "filter-dropdown-menu category-search-menu",
    listClass: "filter-dropdown-menu-list category-search-menu-list",
  });
  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const selectedCategory = allCategories.find((category) => category.id === value);
  const normalizedQuery = normalizeSearch(query);
  const visibleCategories = useMemo(
    () => filterCategories(categories, allCategories, normalizedQuery),
    [allCategories, categories, normalizedQuery],
  );
  useEffect(() => {
    if (!dropdown.open || (disableMobileSearchAutoFocus && isMobile)) return;
    const focusTimer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [disableMobileSearchAutoFocus, dropdown.open, isMobile]);

  return (
    <TextField
      select
      className={["category-search-select", className].filter(Boolean).join(" ")}
      size={size}
      label={label}
      value={value}
      disabled={disabled}
      error={error}
      helperText={helperText}
      slotProps={{
        input: { startAdornment },
        inputLabel: { shrink: true },
        select: {
          displayEmpty: true,
          open: dropdown.open,
          onOpen: () => {
            setQuery("");
            dropdown.onOpen();
          },
          onClose: dropdown.onClose,
          renderValue: () =>
            selectedCategory ? (
              <CategorySelectOption category={selectedCategory} indent={false} showIcon={renderValueIcon} />
            ) : (
              emptyLabel
            ),
          MenuProps: dropdown.MenuProps,
        },
      }}
      onChange={(event) => onChange(String(event.target.value))}
    >
      <ListSubheader className="category-search-header" disableSticky={false}>
        <TextField
          inputRef={searchInputRef}
          className="category-search-input"
          size="small"
          value={query}
          placeholder="Search categories"
          autoComplete="off"
          onChange={(event) => setQuery(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </ListSubheader>
      <MenuItem value="">{emptyLabel}</MenuItem>
      {visibleCategories.map((category) => (
        <MenuItem key={category.id} value={category.id}>
          <CategorySelectOption category={category} />
        </MenuItem>
      ))}
      {!visibleCategories.length ? (
        <MenuItem className="category-search-empty" disabled>
          No matching categories
        </MenuItem>
      ) : null}
    </TextField>
  );
}
